import type { DocumentDetailPayload } from '@Application/Beneficiaries/Documents/Actions/documentFetched';
import type { DocumentEntryView } from '@UI/Store/Documents/Views/buildDocumentEntryView';
import type DocumentListEntryView from '@UI/Store/Documents/Views/DocumentListEntryView';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import DocumentSectionView from '@UI/Store/Documents/Views/DocumentSectionView';
import DocumentSourceRefView from '@UI/Store/Documents/Views/DocumentSourceRefView';
import VerificationProgressView from '@UI/Store/Documents/Views/VerificationProgressView';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

/** Walks the field/group/list tree of a set of entries, depth-first, looking for the field carrying
 * this opaque key. A list row's entries are searched the same way as a group's. */
const findFieldInEntries = (entries: readonly DocumentEntryView[], key: string): DocumentFieldView | null => {
	for (const entry of entries) {
		if (entry.type === 'field') {
			if (entry.key === key) {
				return entry;
			}
			continue;
		}

		if (entry.type === 'group') {
			const found = findFieldInEntries(entry.entries, key);
			if (found !== null) {
				return found;
			}
			continue;
		}

		for (const item of entry.items) {
			const found = findFieldInEntries(item.entries, key);
			if (found !== null) {
				return found;
			}
		}
	}

	return null;
};

/** Same walk, looking for the list carrying this opaque key. */
const findListInEntries = (
	entries: readonly DocumentEntryView[],
	key: string,
): DocumentListEntryView | null => {
	for (const entry of entries) {
		if (entry.type === 'field') {
			continue;
		}

		if (entry.type === 'group') {
			const found = findListInEntries(entry.entries, key);
			if (found !== null) {
				return found;
			}
			continue;
		}

		if (entry.key === key) {
			return entry;
		}

		for (const item of entry.items) {
			const found = findListInEntries(item.entries, key);
			if (found !== null) {
				return found;
			}
		}
	}

	return null;
};

/** Depth-first, in the order the server returns the entries. */
const collectFields = (entries: readonly DocumentEntryView[], fields: DocumentFieldView[]): void => {
	for (const entry of entries) {
		if (entry.type === 'field') {
			fields.push(entry);
			continue;
		}

		if (entry.type === 'group') {
			collectFields(entry.entries, fields);
			continue;
		}

		for (const item of entry.items) {
			collectFields(item.entries, fields);
		}
	}
};

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * Does not extend `DocumentView`: that class freezes itself at the end of its own
 * constructor, which would block this one from setting its own fields afterward. Reuses
 * `LabeledValueView`, `VerificationProgressView` and `DocumentSectionView` instead of redeclaring
 * their logic. `documentTypeId` is read off the `documentType` relation (`{ id }`): the label is
 * resolved against `DocumentTypesListView.labelOf`, never carried here.
 */
class DocumentDetailView {
	readonly id: number;
	readonly title: string;
	readonly documentTypeId: number;
	readonly createdAt: string;
	readonly status: LabeledValueView;
	readonly progress: VerificationProgressView;
	readonly templateVersionId: number;
	readonly sourceInterviewIds: readonly number[];
	/** Every source, interviews, documents and imported documents mixed, in the order the server returns them. */
	readonly sources: readonly DocumentSourceRefView[];
	readonly validatedAt: string | null;
	readonly validatedById: number | null;
	/** `false` once the document is verified. */
	readonly canValidate: boolean;
	readonly canDelete: boolean;
	/** The server's reason when `canDelete` is `false`; `null` otherwise. */
	readonly deletionBlockedReason: string | null;
	readonly sections: readonly DocumentSectionView[];

	constructor(payload: DocumentDetailPayload) {
		this.id = payload.id;
		this.title = payload.title;
		this.documentTypeId = payload.documentType.id;
		this.createdAt = payload.createdAt;
		this.status = new LabeledValueView(payload.status);
		this.progress = new VerificationProgressView(payload.progress);
		this.templateVersionId = payload.templateVersion.id;
		this.sources = payload.sources.flatMap((source) => {
			if (source.interview !== null) {
				return [new DocumentSourceRefView('interview', source.interview.id)];
			}

			if (source.document !== null) {
				return [new DocumentSourceRefView('document', source.document.id)];
			}

			return source.importedDocument !== null
				? [new DocumentSourceRefView('imported_document', source.importedDocument.id)]
				: [];
		});
		this.sourceInterviewIds = this.sources
			.filter((source) => source.kind === 'interview')
			.map((source) => source.id);
		this.validatedAt = payload.validatedAt;
		this.validatedById = payload.validatedBy === null ? null : payload.validatedBy.id;
		this.canValidate = payload.canValidate;
		this.canDelete = payload.canDelete;
		this.deletionBlockedReason = payload.deletionBlockedReason;
		this.sections = payload.sections.map((sectionPayload) => new DocumentSectionView(sectionPayload));

		Object.freeze(this);
	}

	/** Searches every section's tree of entries for the field carrying this key — `null` when no
	 * field of the document matches. */
	findFieldByKey(key: string): DocumentFieldView | null {
		for (const section of this.sections) {
			const found = findFieldInEntries(section.entries, key);
			if (found !== null) {
				return found;
			}
		}

		return null;
	}

	/** Searches every section's tree of entries for the list carrying this opaque key — `null` when
	 * none matches. */
	findListByKey(key: string): DocumentListEntryView | null {
		for (const section of this.sections) {
			const found = findListInEntries(section.entries, key);
			if (found !== null) {
				return found;
			}
		}

		return null;
	}

	/** Every field of the applicable sections, depth-first in the server's order; a section that is
	 * `isNotApplicable` is skipped, as the server does not count it. */
	orderedFields(): readonly DocumentFieldView[] {
		const fields: DocumentFieldView[] = [];

		for (const section of this.sections) {
			if (!section.isNotApplicable) {
				collectFields(section.entries, fields);
			}
		}

		return fields;
	}

	/** The first applicable field whose stored value the type check rejected; `null` when none. */
	get firstInvalidField(): DocumentFieldView | null {
		return this.orderedFields().find((field) => field.isRejectedValue) ?? null;
	}

	/** Searches every section's tree of entries for the one carrying the field with this key — reuses
	 * the same tree walk as `findFieldByKey`, only keeping the enclosing section rather than the field
	 * itself. `null` when no section of the document carries it. */
	findSectionByFieldKey(key: string): DocumentSectionView | null {
		for (const section of this.sections) {
			if (findFieldInEntries(section.entries, key) !== null) {
				return section;
			}
		}

		return null;
	}
}

export default DocumentDetailView;
